// Deterministic progression engine (gym-tracker-spec.md section 5). Runs fully
// on-device, instantly, with zero network. No LLM in the hot path. The Cowork
// coach adds judgement on TOP of this; it must never contradict these rules.
//
// Three set structures, double progression, plus stall detection across 3 sessions.

import type { Exercise, GymId, RepRange, SetLog } from './types';
import { workingSets, sessionScore } from './estimate';
import { round2 } from './weight';

export type SuggestionKind =
  | 'no_history'
  | 'add_reps'
  | 'increase_weight'
  | 'amrap_beat_reps'
  | 'amrap_add_load'
  | 'topset_add_reps'
  | 'topset_increase';

export interface Suggestion {
  kind: SuggestionKind;
  // Short, glanceable cue shown next to the exercise.
  message: string;
  // Suggested working weight for the next session, TOTAL kg. null when there is
  // no history yet (use the seed/last-known weight to start).
  targetWeightKg: number | null;
  targetRepRange: RepRange;
  // For ramp_top_backoff: the suggested back-off weight (~ -10% of the top set).
  backoffWeightKg: number | null;
  stalled: boolean;
  stallMessage: string | null;
}

// One past session's record for a single exercise, most-recent FIRST.
export interface ExerciseHistoryPoint {
  date: string;
  sets: SetLog[];
  bodyweightKg: number | null;
  // The per-exercise note Marko left last time (form cue / reminder to himself).
  // Optional so existing constructors/tests need not supply it.
  note?: string;
  // Which gym this point came from. When the engine runs, history is already
  // filtered to a single gym; this is for the cross-gym trend view, which labels
  // points so a jump between gyms doesn't read as a strength change.
  gymId?: GymId | null;
}

const WORKING_RIR_TARGET = 1; // working sets stop at 1 RIR; failure (0) also passes

function allAtOrAboveTop(ws: SetLog[], top: number): boolean {
  return ws.length > 0 && ws.every((s) => s.reps >= top);
}

function allAtTargetRir(ws: SetLog[]): boolean {
  // A working set passes if its RIR is at or below target (failure counts).
  // Missing RIR is treated as "left reps in the tank" (rir 2), which fails.
  return ws.length > 0 && ws.every((s) => (s.rir ?? 2) <= WORKING_RIR_TARGET);
}

// The canonical working weight of a straight-set entry (all sets share it; use
// the most common value to be robust to a stray typo).
function modeWeight(ws: SetLog[]): number {
  const counts = new Map<number, number>();
  for (const s of ws) counts.set(s.weightKg, (counts.get(s.weightKg) ?? 0) + 1);
  let best = ws[0].weightKg;
  let bestCount = 0;
  for (const [w, c] of counts) {
    if (c > bestCount) {
      best = w;
      bestCount = c;
    }
  }
  return best;
}

function topSet(ws: SetLog[]): SetLog {
  return ws.reduce((a, b) => (b.weightKg > a.weightKg ? b : a), ws[0]);
}

// Back-off load for ramp_top_backoff: -10% of the top set. For bodyweight
// movements (weighted dips) the logged weight is only the ADDED load, so the cut
// is taken off the absolute load (bodyweight + added) and converted back, then
// floored onto the exercise's increment grid so it lands on a real implement.
// E.g. 82 kg BW, top +15: (97 * 0.9) - 82 = 5.3 -> +5 kg, not 15 * 0.9 = 13.5.
export function backoffWeightKg(
  topWeightKg: number,
  ex: Pick<Exercise, 'bodyweight' | 'increment'>,
  bodyweightKg: number | null,
): number {
  if (!ex.bodyweight) return round2(topWeightKg * 0.9);
  const bw = bodyweightKg ?? 80;
  const added = (bw + topWeightKg) * 0.9 - bw;
  const step = ex.increment > 0 ? ex.increment : 1;
  return round2(Math.floor(added / step + 1e-9) * step);
}

function detectStall(
  ex: Pick<Exercise, 'bodyweight'>,
  history: ExerciseHistoryPoint[],
): { stalled: boolean; message: string | null } {
  if (history.length < 3) return { stalled: false, message: null };
  const scores = history.slice(0, 3).map((h) => sessionScore(h.sets, ex, h.bodyweightKg));
  // Progress = any improvement within the 3-session window (newest vs the two prior).
  const progressed = scores[0] > scores[1] + 1e-6 || scores[1] > scores[2] + 1e-6;
  if (progressed) return { stalled: false, message: null };
  return {
    stalled: true,
    message: 'No progress in 3 sessions. Consider a deload, technique check, or variation.',
  };
}

export function suggestProgression(
  ex: Exercise,
  history: ExerciseHistoryPoint[],
): Suggestion {
  const range = ex.defaultRepRange;
  const stall = detectStall(ex, history);

  if (history.length === 0) {
    return {
      kind: 'no_history',
      message: 'No history yet. Log a baseline.',
      targetWeightKg: null,
      targetRepRange: range,
      backoffWeightKg: null,
      stalled: false,
      stallMessage: null,
    };
  }

  const last = history[0];
  const ws = workingSets(last.sets);
  if (ws.length === 0) {
    return {
      kind: 'no_history',
      message: 'Last session had no working sets. Log a baseline.',
      targetWeightKg: null,
      targetRepRange: range,
      backoffWeightKg: null,
      stalled: stall.stalled,
      stallMessage: stall.message,
    };
  }

  const base: Pick<Suggestion, 'stalled' | 'stallMessage'> = {
    stalled: stall.stalled,
    stallMessage: stall.message,
  };

  if (ex.structure === 'amrap') {
    // Dips rule: when set 1 reaches the top of the range, add the increment.
    const set1 = ws[0];
    const added = set1.weightKg;
    if (set1.reps >= range.top) {
      const target = round2(added + ex.increment);
      return {
        kind: 'amrap_add_load',
        message: `Set 1 hit ${range.top}. Add ${ex.increment} kg.`,
        targetWeightKg: target,
        targetRepRange: range,
        backoffWeightKg: null,
        ...base,
      };
    }
    const repsLine = ws.map((s) => s.reps).join('/');
    return {
      kind: 'amrap_beat_reps',
      message: `Same load. Beat last reps (${repsLine}).`,
      targetWeightKg: added,
      targetRepRange: range,
      backoffWeightKg: null,
      ...base,
    };
  }

  if (ex.structure === 'ramp_top_backoff') {
    // Leg press / weighted dips: drive the heavy top set (6-8, 1 RIR), back-offs
    // follow at -10% (of absolute load for bodyweight movements).
    const top = topSet(ws);
    if (top.reps >= range.top && (top.rir ?? 2) <= WORKING_RIR_TARGET) {
      const target = round2(top.weightKg + ex.increment);
      return {
        kind: 'topset_increase',
        message: `Top set hit ${range.top} at ${top.rir ?? 0} RIR. Up ${ex.increment} kg; back-offs at -10%.`,
        targetWeightKg: target,
        targetRepRange: range,
        backoffWeightKg: backoffWeightKg(target, ex, last.bodyweightKg),
        ...base,
      };
    }
    return {
      kind: 'topset_add_reps',
      message: `Same top-set weight. Add reps toward ${range.top}.`,
      targetWeightKg: top.weightKg,
      targetRepRange: range,
      backoffWeightKg: backoffWeightKg(top.weightKg, ex, last.bodyweightKg),
      ...base,
    };
  }

  // straight_sets: double progression.
  const weight = modeWeight(ws);
  if (allAtAboveTopAndRir(ws, range.top)) {
    const target = round2(weight + ex.increment);
    return {
      kind: 'increase_weight',
      message: `All sets hit ${range.top} at <=${WORKING_RIR_TARGET} RIR. Add ${ex.increment} kg, reset to ${range.bottom}.`,
      targetWeightKg: target,
      targetRepRange: range,
      backoffWeightKg: null,
      ...base,
    };
  }
  return {
    kind: 'add_reps',
    message: `Same weight. Add reps toward ${range.top}.`,
    targetWeightKg: weight,
    targetRepRange: range,
    backoffWeightKg: null,
    ...base,
  };
}

function allAtAboveTopAndRir(ws: SetLog[], top: number): boolean {
  return allAtOrAboveTop(ws, top) && allAtTargetRir(ws);
}
